import React, { Component } from "react";
import PropTypes from "prop-types";
import { AnnouncementService } from "@services";
import utils from "@utils";
import { Link } from "react-router";
import { ConfirmationModal, ScrollDetect } from "../../../components";
import "./AnnouncementList.scss";

const displayCount = 25;
class AnnouncementList extends Component {
	constructor(props) {
		super(props);
		this.state = {
			announcementList: [],
			pageNumber: 1,
			hasMore: true,
		};
	}

	/** Gets a list of announcements */
	getAnnouncements = () => {
		const { lang } = global;
		const { pageNumber } = this.state;

		AnnouncementService.getAnnouncements({ pageNumber, displayCount })
			.then(({ data }) => {
				const newItems = data.ItemList.map(item => ({ ...item, loading: false }));
				this.setState(prevState => ({
					announcementList:
						prevState.pageNumber > 1
							? [...prevState.announcementList, ...newItems]
							: newItems,
					pageNumber: prevState.pageNumber + 1,
					hasMore: data.ItemList.length >= displayCount,
				}));
			})
			.catch(() => {
				this.setState({ announcementList: [], pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: lang.t("Error loading Announcements"),
					title: "Error",
				});
			});
	};

	/**
	 * Displays a modal prompting the user to confirm their action
	 * @param {Number} announcementId The unique id of the announcement being deleted
	 */
	handleDelete = announcementId => {
		const { lang } = global;
		this._confirmationModal.openModal({
			title: lang.t("Delete Announcement"),
			message: lang.t("Are you sure you want to delete this announcement_"),
			confirmButton: lang.t("Delete Announcement"),
			onConfirm: () => this.deleteAnnouncement(announcementId),
		});
	};

	/**
	 * Deletes the selected announcment from the list
	 * @param {Number} announcementId The unique id of the announcement being deleted
	 */
	deleteAnnouncement = announcementId => {
		const { lang } = global;

		this.setState(prevState => ({
			announcementList: prevState.announcementList.map(item =>
				item.Id === announcementId ? { ...item, loading: true } : item,
			),
		}));

		AnnouncementService.deleteAnnouncement(announcementId)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Announcement deleted"),
				});
				this.setState(prevState => ({
					announcementList: prevState.announcementList.filter(
						a => a.Id !== announcementId,
					),
				}));
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error deleting Announcements"),
					title: "Error",
				});
				this.setState(prevState => ({
					announcementList: prevState.announcementList.map(item =>
						item.Id === announcementId ? { ...item, loading: false } : item,
					),
				}));
			});
	};

	render() {
		const { lang } = global;
		const { announcementList, hasMore } = this.state;
		const { user, router } = this.props;

		const formatOptions = {
			year: "numeric",
			month: "numeric",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		};

		return (
			<div
				className="container"
				id="announcement-list"
			>
				{/* Title */}
				<div className="row">
					<div className="col-sm-9">
						<h2>{lang.t("Announcements")}</h2>
					</div>
					<div className="col-xs-6 col-sm-3 text-right">
						<Link
							id="new-announcement"
							className="btn btn-primary"
							to="/announcements/add"
						>
							{`+ ${lang.t("New Announcement")}`}
						</Link>
					</div>
				</div>
				{/* Title */}

				<div className="row hidden-xs column-headers">
					<div className="col-sm-1">
						<strong>{lang.t("ID")}</strong>
					</div>
					<div className="col-sm-2 col-lg-3">
						<strong>{lang.t("Subject")}</strong>
					</div>
					<div className="col-sm-3">
						<strong>{lang.t("Schedule")}</strong>
					</div>
					<div className="col-sm-3">
						<strong>{lang.t("Created")}</strong>
					</div>
				</div>

				{/* Table */}
				<div className="list-body">
					<For
						each="announcement"
						of={announcementList}
					>
						<Link
							to={`/announcements/${announcement.Id}`}
							key={`announcement_${announcement.Id}`}
						>
							<div className="row">
								<div className="col-xs-6 col-xs-push-6 col-sm-1 col-sm-push-0">
									<span className="visible-xs-inline">{lang.t("ID")}:&nbsp;</span>
									<span>{announcement.Id}</span>
								</div>
								<div className="col-sm-2 col-lg-3">
									<strong className="visible-xs-inline">
										{`${lang.t("Subject")}: `}
									</strong>
									<Link to={`/announcements/${announcement.Id}/edit`}>
										{announcement.Subject}
									</Link>
								</div>
								<div className="col-sm-3 schedule-date">
									<strong className="visible-xs-inline">
										{`${lang.t("Schedule")}: `}
									</strong>
									<div>
										<div>
											{`${new Date(`${announcement.ActiveStartDateTime}Z`).toLocaleString("en-US", formatOptions)} -`}
										</div>
										<div>
											{`${new Date(`${announcement.ActiveEndDateTime}Z`).toLocaleString("en-US", formatOptions)}`}
										</div>
									</div>
								</div>
								<div className="col-sm-3">
									<span className="visible-xs-inline">
										{lang.t("Created")}&nbsp;
									</span>
									<span>
										{utils.formatDate(
											`${announcement.CreateDateUTC}Z`,
											"en-US",
											user.TimeZoneId,
											"dt-short",
										)}
									</span>
								</div>
								<div className="col-sm-3 col-lg-2">
									<div className="row action-buttons">
										<div className="col-xs-6">
											<button
												type="button"
												className="btn btn-primary btn-block edit-button"
												onClick={e => {
													e.preventDefault();
													router.push(
														`/announcements/${announcement.Id}/edit`,
													);
												}}
											>
												{lang.t("Edit")}
											</button>
										</div>
										<div className="col-xs-6">
											<button
												type="button"
												className="btn btn-danger btn-block delete-button"
												onClick={e => {
													e.preventDefault();
													this.handleDelete(announcement.Id);
												}}
											>
												<Choose>
													<When condition={announcement.loading}>
														<i className="rkf rkf-spinner rkf-spin rkf-fw display-inline-block" />
													</When>
													<Otherwise>{lang.t("Delete")}</Otherwise>
												</Choose>
											</button>
										</div>
									</div>
								</div>
							</div>
						</Link>
					</For>
					<If condition={hasMore}>
						<ScrollDetect
							className="text-center"
							onVisible={this.getAnnouncements}
						>
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</ScrollDetect>
					</If>
				</div>
				{/* Table */}

				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
			</div>
		);
	}
}

AnnouncementList.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
};

AnnouncementList.defaultProps = {
	user: {},
	router: {},
};

export default AnnouncementList;
